import { useQuery } from '@tanstack/react-query';
import { useOeuvres, useEditionsOeuvre } from '../../api/oeuvres.js';
import { useMediasDeRattachement } from '../../api/medias.js';
import { coffreApi } from '../../api/coffre.js';
import { useCouts } from '../../api/provenance.js';
import { cleMetier, appelDeRequete } from '../../api/cles.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, PriceInput, Select } from '@artosera/ui';
import { useCreerAvanceArtiste, type NatureAvance } from '../../api/comptesArtistes.js';
import type { ContactPresente } from '../../api/contacts.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { Field } from '../Field.js';
import { messageCompte } from './compteUi.js';
import styles from './ComptesArtistes.module.css';

export function FormulaireAvance({
  galerieSlug,
  artistes,
  avecCouts,
  avecCoffre,
  onCreee,
}: {
  galerieSlug: string;
  artistes: { artisteId: string; nom: string }[];
  avecCouts: boolean;
  avecCoffre: boolean;
  onCreee: (id: string) => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const creer = useCreerAvanceArtiste(galerieSlug);
  const [artisteId, setArtisteId] = useState(artistes[0]?.artisteId ?? '');
  const [nature, setNature] = useState<NatureAvance>('fonderie');
  const [montant, setMontant] = useState<number | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [regle, setRegle] = useState<'sur_ventes' | 'hors_ventes'>('sur_ventes');
  const [plafond, setPlafond] = useState('');
  const [oeuvreId, setOeuvreId] = useState('');
  const [editionId, setEditionId] = useState('');
  const [fournisseur, setFournisseur] = useState<ContactPresente | null>(null);
  const [pieceId, setPieceId] = useState('');
  const [pieceType, setPieceType] = useState<'media' | 'document'>('media');
  const [coutId, setCoutId] = useState('');
  const [erreur, setErreur] = useState('');
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const oeuvres = useOeuvres(
    galerieSlug,
    { artisteId, q: rechercheDebattue || undefined, parPage: 200 },
    { enabled: !!artisteId },
  );
  const editions = useEditionsOeuvre(galerieSlug, oeuvreId || null);
  const couts = useCouts(galerieSlug, oeuvreId, avecCouts && !!oeuvreId);
  const medias = useMediasDeRattachement(
    galerieSlug,
    oeuvreId ? 'artwork' : 'artist',
    oeuvreId || artisteId || null,
  );
  const documents = useQuery({
    queryKey: cleMetier(galerieSlug, 'coffre', 'pieces-avance', artisteId),
    queryFn: (c) =>
      coffreApi.lister(
        galerieSlug,
        { rattache: `artiste:${artisteId}`, parPage: 200 },
        appelDeRequete(c),
      ),
    enabled: avecCoffre && pieceType === 'document' && !!artisteId,
  });
  const aucune = { value: '', label: label('sansLien') };
  const pieces =
    (pieceType === 'media'
      ? medias.data?.map((m) => ({ value: m.id, label: m.nomFichier }))
      : documents.data?.documents.map((d) => ({ value: d.id, label: d.titre }))) ?? [];
  const erreursLecture = [
    oeuvres.error,
    editions.error,
    couts.error,
    medias.error,
    documents.error,
  ].filter(Boolean);
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        setErreur('');
        void creer
          .mutateAsync({
            artisteId,
            nature,
            date,
            regle,
            ...(montant !== null ? { montantCentimes: montant } : {}),
            ...(regle === 'sur_ventes' && plafond
              ? { plafondPdb: Math.round(Number(plafond) * 100) }
              : {}),
            ...(oeuvreId ? { oeuvreId } : {}),
            ...(editionId ? { editionId } : {}),
            ...(fournisseur ? { fournisseurId: fournisseur.id } : {}),
            ...(pieceId ? { piece: { type: pieceType, id: pieceId } } : {}),
            ...(avecCouts && coutId ? { coutProductionId: coutId } : {}),
          })
          .then((r) => onCreee(r.avance.id))
          .catch((e: unknown) => setErreur(messageCompte(e, t('commun:erreurReseau'))));
      }}
    >
      <Field htmlFor="avance-artiste" label={label('artiste')}>
        <Select
          id="avance-artiste"
          value={artisteId}
          onValueChange={(id) => {
            setArtisteId(id);
            setOeuvreId('');
            setEditionId('');
            setCoutId('');
            setPieceId('');
            setRecherche('');
          }}
          options={artistes.map((a) => ({ value: a.artisteId, label: a.nom }))}
        />
      </Field>
      <Field htmlFor="avance-nature" label={label('nature')}>
        <Select
          id="avance-nature"
          value={nature}
          onValueChange={(v) => setNature(v as NatureAvance)}
          options={(['fonderie', 'tirage', 'encadrement', 'transport', 'autre'] as const).map(
            (v) => ({ value: v, label: label(`natures.${v}`) }),
          )}
        />
      </Field>
      <Field htmlFor="avance-montant" label={label('montant')}>
        <PriceInput
          id="avance-montant"
          cents={montant}
          optional
          onCentsChange={setMontant}
          lang={langue}
          required={!coutId}
        />
      </Field>
      <Field htmlFor="avance-date" label={label('date')}>
        <Input
          id="avance-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />
      </Field>
      <Field htmlFor="avance-regle" label={label('regle')}>
        <Select
          id="avance-regle"
          value={regle}
          onValueChange={(v) => setRegle(v as typeof regle)}
          options={['sur_ventes', 'hors_ventes'].map((v) => ({
            value: v,
            label: label(`regles.${v}`),
          }))}
        />
      </Field>
      {regle === 'sur_ventes' && (
        <Field htmlFor="avance-plafond" label={label('plafond')}>
          <Input
            id="avance-plafond"
            type="number"
            min="0.01"
            max="100"
            step="0.01"
            value={plafond}
            onChange={(e) => setPlafond(e.target.value)}
          />
        </Field>
      )}
      <Field htmlFor="avance-recherche" label={label('rechercherOeuvre')}>
        <Input
          id="avance-recherche"
          type="search"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </Field>
      <Field htmlFor="avance-oeuvre" label={label('oeuvre')}>
        <Select
          id="avance-oeuvre"
          value={oeuvreId}
          onValueChange={(id) => {
            setOeuvreId(id);
            setEditionId('');
            setCoutId('');
            setPieceId('');
          }}
          options={[
            aucune,
            ...(oeuvres.data?.oeuvres
              .filter((o) => o.artisteId === artisteId)
              .map((o) => ({
                value: o.id,
                label: `${o.numeroInventaire} · ${o.titre}`,
              })) ?? []),
          ]}
        />
      </Field>
      {oeuvreId && (
        <Field htmlFor="avance-edition" label={label('edition')}>
          <Select
            id="avance-edition"
            value={editionId}
            onValueChange={setEditionId}
            options={[
              aucune,
              ...(editions.data?.editions.map((e) => ({ value: e.id, label: e.numero })) ?? []),
            ]}
          />
        </Field>
      )}
      <div>
        <p>{label('fournisseur')}</p>
        <RechercheContact
          galerieSlug={galerieSlug}
          contact={fournisseur}
          onChoisir={setFournisseur}
          onEffacer={() => setFournisseur(null)}
        />
      </div>
      <Field htmlFor="avance-piece-type" label={label('typePiece')}>
        <Select
          id="avance-piece-type"
          value={pieceType}
          onValueChange={(v) => {
            setPieceType(v as typeof pieceType);
            setPieceId('');
          }}
          options={(avecCoffre ? ['media', 'document'] : ['media']).map((v) => ({
            value: v,
            label: label(v),
          }))}
        />
      </Field>
      <Field htmlFor="avance-piece" label={label('piece')}>
        <Select
          id="avance-piece"
          value={pieceId}
          onValueChange={setPieceId}
          options={[aucune, ...pieces]}
        />
      </Field>
      {avecCouts && oeuvreId && (
        <Field htmlFor="avance-cout" label={label('coutProduction')}>
          <Select
            id="avance-cout"
            value={coutId}
            onValueChange={(id) => {
              setCoutId(id);
              const cout = couts.data?.couts.find((c) => c.id === id);
              if (cout) {
                setMontant(cout.montantCentimes);
                setEditionId(cout.exemplaireId ?? '');
              }
            }}
            options={[
              aucune,
              ...(couts.data?.couts
                .filter((c) => c.devise === 'EUR')
                .map((c) => ({ value: c.id, label: `${c.libelle} · ${c.fournisseur}` })) ?? []),
            ]}
          />
        </Field>
      )}
      {oeuvres.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
      {erreursLecture.map((e, index) => (
        <p key={index} role="alert">
          {messageCompte(e, t('commun:erreurReseau'))}
        </p>
      ))}
      {erreur && <p role="alert">{erreur}</p>}
      <Button
        type="submit"
        loading={creer.isPending}
        disabled={!artisteId || (!coutId && (montant === null || montant <= 0))}
      >
        {label('creerAvance')}
      </Button>
    </form>
  );
}
